import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import * as Yup from 'yup'

import DialogHeader from '@/shared/DialogHeader'
import Typography from '@mui/material/Typography'


const mapStateToProps = ({ loading }) => ({
  isLoading: loading['plan/editPlan'],
  isPlanLoading: loading['plan/getPlanLevel'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '', // 不加冒號或其他符號
    justifyContent: 'center', // 將 label 與 input 靠上對齊
    direction: 'column', // label 在上，input 在下
    alignItems: 'stretch', // input 撐滿整個欄位
    labelProps: {
      sx: {
        mb: -2, // 調小 label 與 input 的距離
        lineHeight: 1.2, // 行高
        fontWeight: 400, // 適中，不要太輕
        fontSize: 12, // 確保字體大小統一
      },
    },
    childrenSx: {
      width: '95%',
      mt: 0, // 去掉 input 上方的多餘間距
    },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const value = args?.value || ''
    const maxLength = args?.inputProps?.maxLength || 0

    if (!maxLength) return null

    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '12px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const ViewPlanDialog = ({
  dispatch,
  isLoading,
  isPlanLoading,
  onClose,
  onComplete,
  data,
  ...props
}) => {
  const form = useRef()
  const { t } = useTranslation()
  const [planResults, setPlanResults] = useState([])
  const [planMap, setPlanMap] = useState({})

  const handleClose = () => {
    const { reset } = form.current
    reset?.()
    onClose?.()
  }

  // const handleSubmit = () => {
  //   const { handleSubmit: formSubmit } = form.current

  //   formSubmit?.(values => {
  //     const {
  //       companyId,
  //       companyName,
  //       planId,
  //       emailCount,
  //       preEmailCount,
  //       manualEmailCount,
  //       manualPreEmailCount,
  //       status,
  //     } = values

  //     const selectedPlan = planMap?.[planId]

  //     const payload = {
  //       companyId,
  //       companyName,
  //       planId,
  //       manualEmailCount,
  //       manualPreEmailCount,
  //       status,
  //     }

  //     // ✅ 只有人工修改才加進 payload
  //     if (
  //       selectedPlan &&
  //       Number(emailCount) !== Number(selectedPlan.emailCount)
  //     ) {
  //       payload.customEmailCount = emailCount
  //     }

  //     if (
  //       selectedPlan &&
  //       Number(preEmailCount) !== Number(selectedPlan.preEmailCount)
  //     ) {
  //       payload.customPreEmailCount = preEmailCount
  //     }

  //     dispatch({
  //       type: 'plan/editPlan',
  //       payload,
  //       successCallback: () => {
  //         dispatch({
  //           type: 'app/notify',
  //           payload: { message: t('編輯公司方案成功'), severity: 'success' },
  //         })
  //         onClose?.()
  //         onComplete?.()
  //       },
  //       errorCallback: () => {
  //         dispatch({
  //           type: 'app/notify',
  //           payload: {
  //             message: t('編輯公司方案失敗'),
  //             severity: 'error',
  //           },
  //         })
  //       },
  //     })
  //   })?.()
  // }

  const getPlanList = () => {
    if (!isPlanLoading) {
      dispatch({
        type: 'plan/getPlanLevel',
        successCallback: response => {
          const responseData = response?.data || []

          const planOptions = responseData.map(plan => ({
            value: plan.id,
            label: plan.name,
          }))

          const map = {}
          responseData.forEach(plan => {
            map[plan.id] = plan
          })

          setPlanResults(planOptions)
          setPlanMap(map)

          const defaultPlan = map[0]

          if (defaultPlan && form.current) {
            form.current.setValue?.('customEmailCount', defaultPlan.emailCount)
            form.current.setValue?.('customPreEmailCount', defaultPlan.preEmailCount)
          }
        }
      })
    }
  }

  useEffect(() => {
    if (props.open) {
      getPlanList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])


  useEffect(() => {
    if (props.open && planResults.length && form.current && data) {
      const { setValue } = form.current

      setValue?.('companyId', data.companyId)
      setValue?.('companyName', data.companyName)
      setValue?.('planId', data.planId)
      setValue?.('emailCount', data.emailCount)
      setValue?.('manualEmailCount', data.manualEmailCount)
      setValue?.('preEmailCount', data.preEmailCount)
      setValue?.('manualPreEmailCount', data.manualPreEmailCount)
      setValue?.('status', data.planStatus)

    }
  }, [props.open, planResults, data])


  return (
    <Dialog {...props} maxWidth={'xs'}
      PaperProps={{
        sx: {
          borderRadius: 5, // ← 圓角在這裡才有效
          overflow: 'hidden', //  必加，不然 Header 會蓋掉圓角
        },
      }}>

      <DialogHeader
        title={t('general.view_company_plan')}
        onClose={handleClose}
      />

      <DialogContent sx={{ width: 450, pr: 1 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 12 }}
          formConfig={{ mode: 'submit' }}
          formItems={[
            {
              label: t('general.company_name'),
              name: 'companyName',
              defaultValue: '',
              disabled: true,
              validator: Yup.mixed()
                .test(
                  'required',
                  t('validation.company_required'),
                  value => value !== null && value !== undefined && value !== ''
                ),
              inputConfig: {
                ...inputConfig,
                inputProps: { maxLength: 40 }
              },
            },
            {
              label: t('general.plan'),
              name: 'planId',
              type: 'select',
              defaultValue: null,
              disabled: true,
              inputConfig: {
                ...inputConfig,
                options: planResults,
                onChange: e => {
                  const selectedValue = Number(
                    e?.target?.value ?? e ?? 0
                  )
                  const selectedPlan = planMap[selectedValue]

                  if (selectedPlan && form.current) {
                    form.current.setValue?.('emailCount', selectedPlan.emailCount)
                    form.current.setValue?.('preEmailCount', selectedPlan.preEmailCount)
                  }
                }
              },
            },
            {
              label: t('general.send_quantity'),
              name: 'emailCount',
              defaultValue: '',
              disabled: true,
              validator: Yup.number()
                .typeError(t('validation.send_quantity_required'))
                .required(t('validation.send_quantity_required'))
                .min(1, t('validation.send_quantity_min')),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.remaining_letters'),
              name: 'manualEmailCount',
              defaultValue: '',
              disabled: true,
              validator: Yup.number()
                .typeError(t('validation.remaining_letters_required'))
                .required(t('validation.remaining_letters_required'))
                .min(1, t('validation.remaining_letters_min')),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.test_quantity'),
              name: 'preEmailCount',
              defaultValue: '',
              disabled: true,
              validator: Yup.number()
                .typeError(t('validation.test_quantity_required'))
                .required(t('validation.test_quantity_required'))
                .min(1, t('validation.test_quantity_min')),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.remaining_pre_letters'),
              name: 'manualPreEmailCount',
              defaultValue: '',
              disabled: true,
              validator: Yup.number()
                .typeError(t('validation.remaining_pre_letters_required'))
                .required(t('validation.remaining_pre_letters_required'))
                .min(1, t('validation.remaining_pre_letters_min')),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.status'),
              name: 'status',
              type: 'select',
              defaultValue: 'A',
              disabled: true,
              inputConfig: {
                ...inputConfig,
                options: [
                  { label: t('status.ACTIVE'), value: 'A' },
                  { label: t('status.INACTIVE'), value: 'IA' },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'flex-end', //  右下角
        }}>
        <Button
          style={{ minWidth: 100, backgroundColor: '#2D2D6C' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            onClose()
          }}
        >
          {t('general.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(ViewPlanDialog)